import {
  type UserJobType,
  type UserLevelType,
} from "@redotech/account-common/job-level";
import {
  resolveGroupKeysFromJobs,
  resolveJobsFromGroupKeys,
} from "@redotech/redo-merchant-app-common/onboarding/job-groups";
import { JobLevelSelector } from "@redotech/redo-merchant-app-common/onboarding/job-level-selector";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { JOB_LEVEL_BACKFILL_MODAL_UI_SELECTOR } from "./constants";
import * as css from "./job-level-backfill-modal.module.css";

export interface JobLevelBackfillModalProps {
  initialJob?: readonly UserJobType[] | null;
  initialLevel?: UserLevelType | null;
  saving: boolean;
  error: boolean;
  onSave: (value: { job: UserJobType[]; level: UserLevelType }) => void;
}

function ignoreModalClose(): void {}

export const JobLevelBackfillModal = memo(function JobLevelBackfillModal({
  initialJob,
  initialLevel,
  saving,
  error,
  onSave,
}: JobLevelBackfillModalProps) {
  const [jobGroupKeys, setJobGroupKeys] = useState<ReadonlySet<string>>(() =>
    resolveGroupKeysFromJobs(initialJob),
  );
  const [level, setLevel] = useState<UserLevelType | null>(
    initialLevel ?? null,
  );

  const toggleJobGroup = useCallback((key: string) => {
    setJobGroupKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectedJobs = useMemo(
    () => resolveJobsFromGroupKeys(jobGroupKeys),
    [jobGroupKeys],
  );
  // Guard `level != null` inline so TypeScript narrows it to UserLevelType
  // inside the handler; a separate `canSave` const would not narrow here.
  const handleSave =
    selectedJobs.length > 0 && level != null
      ? () => onSave({ job: selectedJobs, level })
      : undefined;
  const canSave = handleSave != null && !saving;

  return (
    <RedoModal
      disableBackdropClose
      disableEscapeClose
      hideCloseButton
      hideHeader
      isOpen
      modalSize="md"
      muiClassName={css.backdropBlur}
      onModalCloseRequested={ignoreModalClose}
      primaryButton={{
        text: "Save",
        disabled: !canSave,
        pending: saving,
        size: "lg",
        onClick: () => handleSave?.(),
      }}
      title="Tell us about your role"
      uiSelector={JOB_LEVEL_BACKFILL_MODAL_UI_SELECTOR}
    >
      <Flex dir="column" gap="xl" p="3xl">
        <Flex align="flex-start" dir="column" gap="sm">
          <RedoBadge color="error" size="sm" text="Required to continue" />
          <Text as="h2" fontSize="xl" fontWeight="semibold" textColor="primary">
            Tell us about your role
          </Text>
          <Text fontSize="sm" textColor="secondary">
            Select every area you work in, then choose your level.
          </Text>
        </Flex>
        <JobLevelSelector
          alwaysShowLevel
          jobGroupKeys={jobGroupKeys}
          level={level}
          onLevelChange={setLevel}
          onToggleJobGroup={toggleJobGroup}
        />
        {error && (
          <Text fontSize="sm" textColor="error">
            Could not save. Please try again.
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
});
